// Remove this file after the Account_Provisioning_History release.
import React, {useCallback, useMemo, useState} from 'react';

import {useAccountActiveUserHistoryEventsByDate} from 'src/hooks/account-active-user-history-events';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {ScrollStackedChart, defaultChartConfig, palette} from '@pb/kit-component-chart';
import {DropdownMenu, DropdownMenuItem} from '@pb/kit-component-dropdown-menu';
import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';

import {UnifiedEventsTable} from './unified-events-table';
import {useLegacyAccountUserHistoryChartData} from './useLegacyAccountUserHistoryChartData';

import type {DataPlotClickEvent, ScrollStackedChartConfig} from '@pb/kit-component-chart';
import type {LegendItemClickedEvent} from '@pb/kit-component-chart/common-types/generated-event-types/events';

type DataPlotClickEventObj = Parameters<DataPlotClickEvent>[0];
type LegendItemClickedEventObj = Parameters<LegendItemClickedEvent>[0];

type Props = {
    accountId: number,
};

const CHART_HEIGHT = 300;

const DAY_RANGE_OPTIONS = [10, 20, 30, 60, 90] as const;
type DayRange = typeof DAY_RANGE_OPTIONS[number];

const getVisiblePlotOptions = (days: DayRange): readonly number[] => {
    switch (days) {
        case 10:
            return [] as const;
        case 20:
            return [10, 20] as const;
        case 30:
            return [10, 15, 30] as const;
        case 60:
            return [15, 30, 60] as const;
        case 90:
            return [15, 30, 45, 90] as const;
        default:
            return [] as const;
    }
};

type VisiblePlots = number;

const toTodayIso = (): string => new Date().toISOString().slice(0, 10);

const formatDay = (isoDay: string): string => {
    const date = new Date(`${isoDay}T00:00:00Z`);
    return date.toLocaleDateString('en-US', {month: 'short', day: 'numeric', timeZone: 'UTC'});
};

export const LegacyAccountUserHistoryChart = ({accountId}: Props) => {
    const [days, setDays] = useState<DayRange>(10);
    const [visiblePlots, setVisiblePlots] = useState<VisiblePlots>(10);
    const [selectedDate, setSelectedDate] = useState(toTodayIso);
    const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

    const visiblePlotsOptions = useMemo(() => getVisiblePlotOptions(days), [days]);

    const effectiveVisiblePlots = useMemo(() => {
        if (!visiblePlotsOptions.length) {
            return days;
        }
        if (!visiblePlotsOptions.includes(visiblePlots)) {
            return visiblePlotsOptions[0];
        }
        return visiblePlots;
    }, [days, visiblePlots, visiblePlotsOptions]);

    const {rows, isLoading} = useLegacyAccountUserHistoryChartData({accountId, days});

    const effectiveSelectedDate = useMemo(() => {
        const lastDay = rows.length ? rows[rows.length - 1].day : toTodayIso();
        const firstDay = rows.length ? rows[0].day : lastDay;
        if (selectedDate < firstDay || selectedDate > lastDay) {
            return lastDay;
        }
        return selectedDate;
    }, [rows, selectedDate]);

    const {data: eventsData, isLoading: eventsLoading} = useAccountActiveUserHistoryEventsByDate({
        accountId,
        historyDate: effectiveSelectedDate,
    });

    const allDatasets = useMemo(() => [
        {
            seriesName: 'Licensed Users',
            color: palette[1],
            data: rows.map(row => ({value: row.licensed})),
        },
        {
            seriesName: 'Mobile Users',
            color: palette[3],
            data: rows.map(row => ({value: row.mobile})),
        },
        {
            seriesName: 'CRM Users',
            color: palette[4],
            data: rows.map(row => ({value: row.crm})),
        },
        {
            seriesName: 'Alert Users',
            color: palette[8],
            data: rows.map(row => ({value: row.alert})),
        },
        {
            seriesName: 'Newsletter Users',
            color: palette[7],
            data: rows.map(row => ({value: row.newsletter})),
        },
    ], [rows]);

    const dataSourceProps = useMemo(() => ({
        categories: [{
            category: rows.map(row => ({label: formatDay(row.day)})),
        }],
        dataset: allDatasets.map(ds =>
            hiddenSeries.has(ds.seriesName)
                ? {...ds, initiallyHidden: '1', data: []}
                : ds,
        ),
    }), [rows, allDatasets, hiddenSeries]);

    const handleDataPlotClick = useCallback((eventObj: DataPlotClickEventObj) => {
        const clickedRow = rows[eventObj.data.dataIndex];
        if (clickedRow) {
            setSelectedDate(clickedRow.day);
        }
    }, [rows]);

    const handleLegendItemClicked = useCallback((eventObj: LegendItemClickedEventObj) => {
        eventObj.preventDefault();
        const seriesName = eventObj.data.datasetName;
        setHiddenSeries(prev => {
            const next = new Set(prev);
            if (next.has(seriesName)) {
                next.delete(seriesName);
            } else {
                next.add(seriesName);
            }
            return next;
        });
    }, []);

    const chartConfig: ScrollStackedChartConfig = useMemo(() => ({
        ...defaultChartConfig,
        yAxisName: 'Users',
        numVisiblePlot: effectiveVisiblePlots,
        labelDisplay: 'rotate',
        showSum: 0,
        scrollToEnd: 1,
    }), [effectiveVisiblePlots]);

    const chartEvents = useMemo(() => ({
        dataPlotClick: handleDataPlotClick,
        legendItemClicked: handleLegendItemClicked,
    }), [handleDataPlotClick, handleLegendItemClicked]);

    return (
        <Group gap="xl" direction="v">
            <FormSection
                headline={(
                    <Group justify="space-between" alignItems="center" gap="xs" style={{minWidth: '65vw'}}>
                        <Group gap="xs" alignItems="center" justify="start">
                            <span>Users Count History</span>
                            <DropdownMenu
                                anchor={({positionRef, getAnchorProps, anchorRef}) => (
                                    <span ref={anchorRef} {...getAnchorProps()} style={{cursor: 'pointer'}}>
                                        <Group gap="xxs" alignItems="center">
                                            <span>{`(Last ${days} days)`}</span>
                                            <Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>
                                        </Group>
                                    </span>
                                )}>
                                {DAY_RANGE_OPTIONS.map(value => (
                                    <DropdownMenuItem
                                        key={value}
                                        caption={`${value} days`}
                                        onClick={() => setDays(value)}
                                    />
                                ))}
                            </DropdownMenu>
                        </Group>
                        {visiblePlotsOptions.length ? (
                            <Group>
                                <DropdownMenu
                                    anchor={({positionRef, getAnchorProps, anchorRef}) => (
                                        <span ref={anchorRef} {...getAnchorProps()} style={{cursor: 'pointer'}}>
                                            <Group gap="xxs" alignItems="center">
                                                <span>{`Show ${effectiveVisiblePlots}`}</span>
                                                <Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>
                                            </Group>
                                        </span>
                                    )}>
                                    {visiblePlotsOptions.map(value => (
                                        <DropdownMenuItem
                                            key={value}
                                            caption={`${value} columns`}
                                            onClick={() => setVisiblePlots(value)}
                                        />
                                    ))}
                                </DropdownMenu>
                            </Group>
                        ) : null}
                    </Group>
                )}>
                <BoxLoading loading={isLoading}>
                    <ScrollStackedChart
                        key={effectiveVisiblePlots}
                        height={CHART_HEIGHT}
                        config={chartConfig}
                        dataSourceProps={dataSourceProps}
                        events={chartEvents}
                    />
                </BoxLoading>
            </FormSection>
            <FormSection headline={`Daily User Changes — ${formatDay(effectiveSelectedDate)}`}>
                <BoxLoading loading={eventsLoading}>
                    <UnifiedEventsTable key={effectiveSelectedDate} eventsData={eventsData}/>
                </BoxLoading>
            </FormSection>
        </Group>
    );
};
